import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Header,
    UserList,
    Spinner,
    Pagination,
    Row,
    LabPicker,
    ClinicPicker,
    Toggle,
} from '../../components';
import {
    fetchAllClinics,
    fetchAllLabs,
    http,
} from '../../helpers';
import './styles.scss';

const getInitialPagination = () => ({
    number: 0,
    totalPages: 0,
    first: null,
    last: null,
});

export default function Users() {
    const navigate = useNavigate();
    const [users, setUsers] = useState(null);
    const [pagination, setPagination] = useState(getInitialPagination());
    const [filters, setFilters] = useState({
        clinicId: '',
        labId: '',
        enabled: true,
    });
    const [clinics, setClinics] = useState([]);
    const [labs, setLabs] = useState([]);
    const [fetching, setFetching] = useState(false);
    const [pageNum, setPageNum] = useState(0);
    const [clinic, setClinic] = useState('');
    const [lab, setLab] = useState('');
    const [enabled, setEnabled] = useState(true);

    const getUsers = (page = pageNum, nextFilters = filters) => {
        const filterString = Object.keys(nextFilters).map((key) => `${key}=${nextFilters[key]}`).join('&');
        const relativeUrl = `/case-api/users?size=10&page=${page}&${filterString}`;

        setFetching(true);
        http.get(relativeUrl)
            .then(({ data }) => {
                setUsers(data.content);
                setPagination({
                    number: data.number,
                    totalPages: data.totalPages,
                    first: data.first,
                    last: data.last,
                });
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                setFetching(false);
            });
    };

    const disableUser = (id, disable) => {
        const action = disable === true ? 'disable' : 'enable';
        return http.post(`/case-api/users/${id}/${action}`);
    };

    useEffect(() => {
        getUsers(pageNum, filters);
    }, [pageNum, filters]);

    useEffect(() => {
        Promise.all([fetchAllClinics(), fetchAllLabs()])
            .then(([allClinics, allLabs]) => {
                setClinics(allClinics);
                setLabs(allLabs);
            })
            .catch((error) => console.warn(error));
    }, []);

    const onAddUser = () => {
        navigate('/create-user');
    };
    const onFilterLab = (labId) => {
        setFilters((currentFilters) => ({ ...currentFilters, labId }));
        setLab(labId);
    };
    const onFilterClinic = (clinicId) => {
        setFilters((currentFilters) => ({ ...currentFilters, clinicId }));
        setClinic(clinicId);
    };
    const onFilterEnabled = (evt) => {
        const val = evt.target.checked;
        setFilters((currentFilters) => ({ ...currentFilters, enabled: val }));
        setEnabled(val);
    };
    const onDeleteUser = (id) => {
        http.delete(`/case-api/users/${id}`)
            .then((res) => {
                if (res.status === 200) {
                    getUsers();
                }
            })
            .catch((error) => {
                console.warn(error);
            });
    };
    const onSaveUser = (userToSave, nextEnabled) => {
        http.put(`/case-api/users/${userToSave.id}`, userToSave)
            .then((res) => {
                if (res.status !== 200) {
                    console.error(res);
                    return;
                }

                if (nextEnabled !== userToSave.enabled) {
                    disableUser(userToSave.id, !nextEnabled)
                        .then(() => getUsers())
                        .catch((error) => console.error(error));
                } else {
                    getUsers();
                }
            })
            .catch((error) => {
                console.warn(error);
            });
    };
    const onDisableUser = (id, disable) => {
        disableUser(id, disable)
            .then(() => getUsers())
            .catch((error) => {
                console.error(error);
            });
    };

    return (
        <>
            <Header label="Users" />
            <main className="dashboard-container">
                <Row>
                    <div className="user-header">
                        <LabPicker labs={labs} onSelectLab={onFilterLab} selectedLab={lab} />
                        <ClinicPicker clinics={clinics} onSelectClinic={onFilterClinic} selectedClinic={clinic} />
                        <Toggle
                            label="Enabled"
                            checked={enabled}
                            onChange={onFilterEnabled}
                        />
                        <i
                            title="Add new user"
                            role="presentation"
                            className="material-icons fz-50 clickable"
                            onClick={onAddUser}
                        >
                            add_circle
                        </i>
                    </div>
                </Row>
                {
                    fetching && <Spinner type="page" />
                }
                {
                    users && !users.length && !fetching
                        ? <p className="text-red text-align-center">No users found</p>
                        : (
                            <UserList
                                users={users}
                                clinics={clinics}
                                labs={labs}
                                onDeleteUser={onDeleteUser}
                                onSaveUser={onSaveUser}
                                onDisableUser={onDisableUser}
                            />
                        )
                }
                <Pagination onChangePage={setPageNum} pagination={pagination} />
            </main>
        </>

    );
}
